import dayjs from 'dayjs';

import { checkRol } from '../security';
import { aprobador, inspector, notificador, proveedorNotificador } from '../security/constantsRols';

import { api } from './apiInstances';

const perfilEjecutaEnum = {
  Proveedor: 0,
  Aprobador: 1,
  Inspector: 2
};

export const getPerfilEjecuta = () => {
  if (checkRol(proveedorNotificador) || checkRol(notificador)) {
    return perfilEjecutaEnum?.Proveedor;
  }
  if (checkRol(inspector)) {
    return perfilEjecutaEnum?.Inspector;
  }
  if (checkRol(aprobador)) {
    return perfilEjecutaEnum?.Aprobador;
  }

  return perfilEjecutaEnum?.Proveedor;
};

export const getLocationProgramPointService = async (
  idParteDiario,
  perfilEjecuta = perfilEjecutaEnum.Proveedor
) => {
  const result = await api.get(
    `/v1/ParteTrabajoDiarios/${idParteDiario}/detalles?perfilEjecuta=${perfilEjecuta}`
  );

  return result?.data?.resultado;
};

const mapProgramPoint = (location, line) => {
  const {
    pozoId,
    pozoNombre,
    nombreEquipo,
    unidadMedicion,
    cantidad,
    eventoSigla,
    habilitaVentana,
    puntoDePrograma = []
  } = location;

  return {
    pozoId,
    pozoNombre,
    nombreEquipo,
    eventoSigla,
    habilitaVentana,
    totalValue: cantidad,
    totalValueFijado: cantidad,
    cantidad,
    unidadMedicion,
    programPoints: puntoDePrograma.map((pp) => ({
      quantity: pp.cantidad,
      points: pp.puntoPrograma || '',
      quantityFijados: pp.cantidad || '',
      pointsFijados: pp.puntoPrograma || '',
      idPartePuntoPrograma: pp.id ?? null,
      idParteDiario: line?.idParteDiario,
      cost: pp.claseCosto
        ? {
            costoClase: pp.claseCosto?.split('-')[0],
            id: pp.claseCosto,
            nombre: `${pp.claseCosto} - ${pp.fase}`,
            label: `${pp.claseCosto} - ${pp.fase}`,
            fase: pp.fase || pp.claseCosto?.split('-')[1]
          }
        : null,
      code: pp.codigo
        ? { nombre: pp.codigo, id: pp.codigo, label: pp.codigo, value: pp.codigo }
        : null,
      subCode: pp.subcodigo
        ? { nombre: pp.subcodigo, id: pp.subcodigo, label: pp.subcodigo, value: pp.subcodigo }
        : null,
      detail: pp.descripcion,
      clasification: pp.clasificacion
        ? {
            nombre: pp.clasificacion,
            id: pp.clasificacion,
            label: pp.clasificacion,
            value: pp.clasificacion
          }
        : null,
      idOperativo: pp.idOperativo ?? null
    }))
  };
};

export const getLocationProgramPointAdapter = async (
  line,
  perfilEjecuta = getPerfilEjecuta() || perfilEjecutaEnum?.Proveedor
) => {
  const locations = await getLocationProgramPointService(line?.idParteDiario, perfilEjecuta);

  return locations.map((location) => mapProgramPoint(location, line));
};

export const postLocationProgramPointService = async (body) => {
  const result = await api.post('/v1/ParteTrabajoDiarios/notificacion', body);

  return result.data;
};

const mapperCostPhase = (cost) => {
  if (!cost) return '';

  return String(
    cost?.costoClase?.split([' '])[0] || cost?.nombre?.split([' - '])[0] || cost?.costoClase
  );
};

// NOSONAR: TODO: change locations name
export const postLocationProgramPointAdapter = async (locationsParams, line) => {
  const { idParteDiario, unidadMedicion } = line;

  const locations = locationsParams?.filter((location) => location?.habilitaVentana);
  const cantidadTotal = locations.reduce((sum, loc) => sum + Number(loc?.totalValue), 0);

  const puntosPrograma = locations.flatMap((location) =>
    location.programPoints.map((pp) => ({
      pozoId: location.pozoId,
      pozoNombre: location.pozoNombre,
      puntoPrograma: Number(pp.points || 0),
      cantidad: Number(pp.quantity || 0),
      claseCosto: mapperCostPhase(pp.cost),
      fase: String(pp.cost?.fase?.trim() || pp.cost?.fase || ''),
      codigoCosto: String(pp.code?.label || pp.code || ''),
      subCodigo: String(pp.subCode?.label || pp.subCode || ''),
      descripcion: String(pp.detail || ''),
      clasificacion: String(pp.clasification?.nombre || '')
    }))
  );

  const body = [
    {
      id: idParteDiario,
      cantidad: cantidadTotal,
      unidadMedida: unidadMedicion || locations.find((loc) => loc.unidadMedicion)?.unidadMedicion,
      puntosPrograma
    }
  ];

  return postLocationProgramPointService(body);
};

export const rectifyLocationProgramPointService = async (
  id,
  body,
  perfilEjecuta = perfilEjecutaEnum.Proveedor
) => {
  const result = await api.put(
    `/v1/ParteTrabajoDiarios/${id}?perfilEjecuta=${perfilEjecuta}`,
    body
  );

  return result.data;
};

export const rectifyLocationProgramPointAdapter = async (
  line,
  cantidad,
  locationsParams = [],
  perfilEjecuta = getPerfilEjecuta() || perfilEjecutaEnum.Proveedor
) => {
  const { idParteDiario, unidadMedicion, comentarios } = line;
  const locations = locationsParams?.filter((location) => location?.habilitaVentana);

  const puntosPrograma = locations.flatMap((location) =>
    location.programPoints.map((pp) => ({
      pozoId: location.pozoId,
      pozoNombre: location.pozoNombre,
      puntoPrograma: Number(pp.points || 0),
      cantidad: Number(pp.quantity || 0),
      claseCosto: mapperCostPhase(pp.cost),
      fase: String(pp.cost?.fase?.trim() || pp.cost?.fase || ''),
      codigoCosto: String(pp.code?.label || pp.code || ''),
      subCodigo: String(pp.subCode?.label || pp.subCode || ''),
      descripcion: String(pp.detail || ''),
      clasificacion: String(pp.clasification?.nombre || '')
    }))
  );
  const body = {
    id: idParteDiario,
    unidadMedida: unidadMedicion,
    perfilEjecuta,
    cantidad,
    puntosPrograma,
    requiereConformidad: line?.requiereConformidad ?? null,
    requiereAdjuntar: line?.requiereAdjuntar ?? null,
    revisionExperta: line?.revisionExperta ?? null,
    comentario: comentarios || ''
  };

  return rectifyLocationProgramPointService(idParteDiario, body, perfilEjecuta);
};

export const approveLocationProgramPointService = async (body) => {
  const result = await api.post(`/v1/ParteTrabajoDiarios/aprobacion`, body);

  return result.data;
};

export const approveLocationProgramPointApdater = async (line, cantidadTotal, locationsParams) => {
  const { idParteDiario } = line;
  const locations = locationsParams?.filter((location) => location?.habilitaVentana);
  const puntosPrograma = locations.flatMap((location) =>
    location.programPoints.map((pp) => {
      const adapterProgram = {
        pozoId: location.pozoId,
        pozoNombre: location.pozoNombre,
        puntoPrograma: Number(pp.points || 0),
        cantidad: Number(pp.quantity || 0),
        claseCosto: mapperCostPhase(pp.cost),
        fase: String(pp.cost?.fase?.trim() || pp.cost?.fase || ''),
        codigoCosto: String(pp.code?.label || pp.code || ''),
        subCodigo: String(pp.subCode?.label || pp.subCode || ''),
        descripcion: String(pp.detail || ''),
        clasificacion: String(pp.clasification?.nombre || '')
      };

      return adapterProgram;
    })
  );

  const body = [
    {
      id: idParteDiario,
      cantidad: Number(cantidadTotal) || 0,
      unidadMedida: line?.unidadMedida,
      puntosPrograma
    }
  ];

  return approveLocationProgramPointService(body);
};

export const approveAllAdapter = async (ids) => {
  const body = ids.map((id) => ({
    id,
    cantidad: 0
  }));

  return approveLocationProgramPointService(body);
};

export const approveAdapter = async (idParteDiario) => {
  const body = [
    {
      id: idParteDiario,
      cantidad: 0
    }
  ];

  return approveLocationProgramPointService(body);
};

export const rejectLocationProgramPointAdapter = async (
  line,
  cantidad,
  comentario,
  locationsParams,
  requiereConformidad = false,
  requiereAdjuntar = false,
  perfilEjecuta = perfilEjecutaEnum.Aprobador
) => {
  const { idParteDiario, unidadMedida } = line;

  const locations = locationsParams?.filter((location) => location?.habilitaVentana);
  const puntosPrograma = locations.flatMap((location) =>
    location.programPoints.map((pp) => ({
      pozoId: location?.pozoId,
      pozoNombre: location?.pozoNombre,
      puntoPrograma: Number(pp?.points || 0),
      cantidad: Number(pp?.quantity || 0),
      claseCosto: mapperCostPhase(pp.cost),
      fase: String(pp?.cost?.fase?.trim() || pp?.cost?.fase || ''),
      codigoCosto: String(pp?.code?.label || pp?.code || ''),
      subCodigo: String(pp?.subCode?.label || pp?.subCode || ''),
      descripcion: String(pp?.detail || ''),
      clasificacion: String(pp?.clasification?.nombre || '')
    }))
  );
  const programPointCopy = puntosPrograma;

  const body = {
    id: idParteDiario,
    unidadMedida,
    perfilEjecuta,
    cantidad,
    puntosPrograma: programPointCopy,
    requiereConformidad,
    requiereAdjuntar,
    comentario
  };

  return rectifyLocationProgramPointService(idParteDiario, body, perfilEjecuta);
};

export const rejectLocationWithoutProgramPointAdapter = async (
  line,
  cantidad,
  comentario,
  requiereConformidad = false,
  requiereAdjuntar = false,
  perfilEjecuta = perfilEjecutaEnum.Aprobador
) => {
  const { idParteDiario, unidadMedida } = line;

  const body = {
    id: idParteDiario,
    unidadMedida,
    perfilEjecuta,
    cantidad: Number(cantidad) || 0,
    requiereConformidad,
    requiereAdjuntar,
    puntosPrograma: [],
    comentario
  };

  return rectifyLocationProgramPointService(idParteDiario, body, perfilEjecuta);
};

const mapProgramPointForEdit = (location, pp) => {
  const costNombre = pp.cost?.nombre || '';
  const claseCosto = costNombre
    ? costNombre.toString().split(' - ')[0] || ''
    : pp.cost?.costoClase || '';
  const fase = pp.cost?.fase ? pp.cost.fase.toString().trim() : '';
  const codigoCosto = pp.code?.label ? pp.code.label.toString() : '';
  const subCodigo = pp.subCode?.label ? pp.subCode.label.toString() : '';
  const descripcion = pp.detail ? pp.detail.toString() : '';
  const clasificacionNombre = pp.clasification?.nombre ? pp.clasification.nombre.toString() : '';

  return {
    pozoId: location.pozoId,
    pozoNombre: location.pozoNombre,
    puntoPrograma: Number(pp.points || 0),
    cantidad: Number(pp.quantity || 0),
    claseCosto,
    fase,
    codigoCosto,
    subCodigo,
    descripcion,
    clasificacion: clasificacionNombre
  };
};

export const editLocationProgramPointAdapter = async (
  line,
  cantidad,
  comentario,
  locationsParams,
  perfilEjecuta = perfilEjecutaEnum.Aprobador
) => {
  const { idParteDiario, unidadMedida } = line;

  const locations = locationsParams?.filter((location) => location?.habilitaVentana);

  const puntosPrograma = locations.flatMap((location) =>
    location.programPoints
      // .filter((pp) => Number(pp.quantity) !== 0)
      .map((pp) => mapProgramPointForEdit(location, pp))
  );

  const programPointCopy = puntosPrograma;

  const body = {
    id: idParteDiario,
    unidadMedida,
    perfilEjecuta,
    cantidad,
    puntosPrograma: programPointCopy,
    comentario
  };

  return rectifyLocationProgramPointService(idParteDiario, body, perfilEjecuta);
};

export const editQuantityAdapter = async (
  line,
  cantidad,
  comentario,
  perfilEjecuta = perfilEjecutaEnum.Aprobador
) => {
  const { idParteDiario, unidadMedida } = line;

  const body = {
    id: idParteDiario,
    unidadMedida,
    perfilEjecuta,
    cantidad: Number(cantidad) || 0,
    puntosPrograma: [],
    comentario
  };

  return rectifyLocationProgramPointService(idParteDiario, body, perfilEjecuta);
};

export const obtainOperativeData = async (body) => {
  const result = await api.post(`/v1/DailyCost/detalleOperativoPredeterminado`, body);

  return result.data;
};

// NOSONAR: This function is used in AppMobile, so it is necessary to keep it in this service

export const downloadPreticket = async ({
  idSolicitud,
  nroContrato,
  mensualizada = false,
  idsUbicaciones = [],
  numero,
  ubicacion,
  servicio
}) => {
  try {
    const payload = {
      IdSolicitud: idSolicitud,
      NroContrato: nroContrato,
      Mensualizada: mensualizada,
      ...(mensualizada && { IdsUbicaciones: idsUbicaciones })
    };

    const result = await api.post('/v1/ParteTrabajoDiarios/preticket', payload, {
      headers: {
        Accept: mensualizada ? 'application/zip, application/pdf, */*' : 'application/pdf',
        'Content-Type': 'application/json'
      },
      responseType: 'blob'
    });

    const contentType = result.headers?.['content-type'];
    const isZip = contentType?.includes('zip');
    const fecha = dayjs().format('DDMMYYYY');

    const fileName = mensualizada
      ? `${numero}-${servicio}.zip`
      : `${numero}_${ubicacion}_${fecha}.pdf`;

    const blob = new Blob([result.data], {
      type: isZip ? 'application/zip' : 'application/pdf'
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      data: result
    };
  } catch (e) {
    // Handle blob error responses that may contain JSON with a message
    if (e?.data instanceof Blob) {
      try {
        const text = await e?.data.text();
        const error = JSON.parse(text);

        if (error?.message) {
          throw new Error(error?.message);
        }
      } catch (error) {
        const mensaje = error?.message || error?.errorMessage || error?.result;

        throw new Error(mensaje);
      }
    }
    throw new Error('Estamos experimentando problemas, por favor, intente más tarde.');
  }
};
